/**
 * Copyright 2019 Atelier Disko. All rights reserved. This source
 * code is distributed under the terms of the BSD 3-Clause License.
 */

import React, { useState, useEffect } from 'react';
import './Werteinheit.css';
import { Client } from '@atelierdisko/dsk';
import { BaseLink, withRoute } from 'react-router5';

// There are two possible ways this component is used, in the first case
// children is a React object, in the second children is a string with
// (escaped) HTML.
//
// We see the first case when the component is used explictly and the second,
// when it is instantiated by the DocTransformer, turning a <pre> into a
// Werteinheit.
function Werteinheit(props) {
  const [data, setData] = useState(null);
  const [image, setImage] = useState(null);
  const [highlight, setHighlight] = useState([0, '100%', 0, 0]);
  const [highlightIsVisible, setHighlightIsVisible] = useState(false);
  const [activeItem, setActiveItem] = useState('');
  const [imageSize, setImageSize] = useState([undefined, undefined]);

  useEffect(() => {
    if (props.src) {
      Client.fetch(props.src).then(d => {
        setImage(d.image);
        setData(d);
      });
    }
  }, [props.src]);

  if (!data) {
    return <div className="werteinheit"></div>;
  }

  let structure = data.structure.map(element => {
    let path = element.title;
    let elementClasses = ['werteinheit-tree__node werteinheit-tree__node--element'];

    if (element.variants) {
      elementClasses.push('werteinheit-tree__node--placeholder');
    }

    if (activeItem.includes(path)) {
      elementClasses.push('werteinheit-tree__node--is-active');
    }

    let content = [
      <BaseLink
        router={props.router}
        routeName="node"
        routeParams={{ node: `${element.link}` }}
        onMouseEnter={() => {
          if (element.image) {
            setImage(element.image);
          } else if (element.states && element.states.length > 0 && element.states[0].image) {
            setImage(element.states[0].image);
          }

          if (element.highlight) {
            setHighlight(element.highlight);
            setHighlightIsVisible(true);
          } else if (element.states && element.states.length > 0 && element.states[0].highlight) {
            setHighlight(element.states[0].highlight);
            setHighlightIsVisible(true);
          } else {
            setHighlightIsVisible(false);
          }

          if (element.states && element.states.length > 0) {
            setActiveItem(path + '/' + element.states[0].title);
          } else {
            setActiveItem(path);
          }
        }}
      >
        {element.title}
      </BaseLink>,
    ];

    if (element.states && element.states.length > 0) {
      let states = element.states.map(state => {
        let path = `${element.title}/${state.title}`;
        let stateClasses = ['werteinheit-tree__node werteinheit-tree__node--state'];

        if (activeItem.includes(path)) {
          stateClasses.push('werteinheit-tree__node--is-active');
        }

        return (
          <li className={stateClasses.join(' ')}>
            <BaseLink
              router={props.router}
              routeName="node"
              routeParams={{ node: `${state.link}` }}
              onMouseEnter={() => {
                if (state.image) {
                  setImage(state.image);
                } else if (element.image) {
                  setImage(element.image);
                }

                if (state.highlight) {
                  setHighlight(state.highlight);
                  setHighlightIsVisible(true);
                } else if (element.highlight) {
                  setHighlight(element.highlight);
                  setHighlightIsVisible(true);
                } else {
                  setHighlightIsVisible(false);
                }

                setActiveItem(path);
              }}
            >
              {state.title}
            </BaseLink>
          </li>
        );
      });

      content.push(<ul className="werteinheit-tree__list--states">{states}</ul>);
    }

    if (element.variants && element.variants.length > 0) {
      let variants = element.variants.map(variant => {
        let path = `${element.title}/${variant.title}`;
        let variantClasses = ['werteinheit-tree__node werteinheit-tree__node--variant'];

        if (activeItem.includes(path)) {
          variantClasses.push('werteinheit-tree__node--is-active');
        }

        let variantContent = [
          <BaseLink
            router={props.router}
            routeName="node"
            routeParams={{ node: `${variant.link}` }}
            onMouseEnter={() => {
              if (variant.image) {
                setImage(variant.image);
              } else if (variant.states && variant.states.length > 0 && variant.states[0].image) {
                setImage(element.states[0].image);
              } else if (element.image) {
                setImage(element.image);
              }

              if (variant.highlight) {
                setHighlight(variant.highlight);
                setHighlightIsVisible(true);
              } else if (variant.states && variant.states.length > 0 && variant.states[0].highlight) {
                setHighlight(variant.states[0].highlight);
                setHighlightIsVisible(true);
              } else if (element.highlight) {
                setHighlight(element.highlight);
                setHighlightIsVisible(true);
              } else {
                setHighlightIsVisible(false);
              }

              if (variant.states && variant.states.length > 0) {
                setActiveItem(path + '/' + variant.states[0].title);
              } else {
                setActiveItem(path);
              }
            }}
          >
            {variant.title}
          </BaseLink>,
        ];

        if (variant.states && variant.states.length > 0) {
          let states = variant.states.map(state => {
            let path = `${element.title}/${variant.title}/${state.title}`;

            let stateClasses = ['werteinheit-tree__node werteinheit-tree__node--state'];

            if (activeItem.includes(path)) {
              stateClasses.push('werteinheit-tree__node--is-active');
            }

            return (
              <li className={stateClasses.join(' ')}>
                <BaseLink
                  router={props.router}
                  routeName="node"
                  routeParams={{ node: `${state.link}` }}
                  onMouseEnter={() => {
                    if (state.image) {
                      setImage(state.image);
                    } else if (variant.image) {
                      setImage(variant.image);
                    } else if (element.image) {
                      setImage(element.image);
                    }

                    if (state.highlight) {
                      setHighlight(state.highlight);
                      setHighlightIsVisible(true);
                    } else if (variant.highlight) {
                      setHighlight(variant.highlight);
                      setHighlightIsVisible(true);
                    } else if (element.highlight) {
                      setHighlight(element.highlight);
                      setHighlightIsVisible(true);
                    } else {
                      setHighlightIsVisible(false);
                    }

                    setActiveItem(path);
                  }}
                >
                  {state.title}
                </BaseLink>
              </li>
            );
          });

          variantContent.push(<ul className="werteinheit-tree__list--states">{states}</ul>);
        }

        return <li className={variantClasses.join(' ')}>{variantContent}</li>;
      });

      content.push(<ul className="werteinheit-tree__list--variants">{variants}</ul>);
    }

    return <li className={elementClasses.join(' ')}>{content}</li>;
  });

  return (
    <div className="werteinheit">
      <div className="werteinheit-title">{data.title}</div>
      <div className="werteinheit-structure">
        <div className="werteinheit-heading">Struktur</div>
        <ul className="werteinheit-tree">{structure}</ul>
      </div>
      <div className="werteinheit-playground">
        <div className="werteinheit-heading werteinheit-playground__heading">Beispiel</div>
        <div className="werteinheit-playground__wrapper">
          <div
            className={`werteinheit-playground__highlight ${
              highlightIsVisible ? 'werteinheit-playground__highlight--is-visible' : ''
            }`}
            style={{
              left: highlight[0],
              width: highlight[1],
              top: highlight[2],
              height: highlight[3],
            }}
          ></div>
          <img
            src={`/api/v1/tree/${props.url}/${image}`}
            style={{ maxWidth: imageSize[0] || 'auto', maxHeight: imageSize[1] || 'auto' }}
            onLoad={ev => {
              let width = ev.target.naturalWidth;
              let height = ev.target.naturalHeight;

              if (image.includes('@2x')) {
                setImageSize([width / 2, height / 2]);
              } else {
                setImageSize([width, height]);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default withRoute(Werteinheit);
